import { Link } from "react-router-dom";
import { ArrowRight, ChevronsLeftRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const KITCHEN_IMG = "/images/kitchen-interior.webp";
const ATLANTA_IMG = "/images/atlanta-site.webp";
const KITCHEN_VIEW1_IMG = "/images/kitchen-view1.webp";
const KITCHEN_VIEW2_IMG = "/images/kitchen-view2.webp";
const EQUATION_IMG = "/images/unit-menu-boards.webp";
const SPACES_IMG = "/images/parking-lot-dusk.webp";
const FOOD_IMG = "/images/food-grill.webp";
const FOOD_COURT_IMG = "/images/food-court.webp";
const DUSK_IMG = "/images/kitchen-dusk-lot.webp";
const GOLDEN_IMG = "/images/kitchen-golden-hour.webp";
const BEFORE_IMG = "/images/before-lot.webp";
const AFTER_IMG = "/images/after-kitchen.webp";
// Shuffle once per page load so similar concepts don't sit next to each other
const shuffled = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Shuffle images, then deal the videos in at even intervals so two clips never sit side by side
const spreadWithVideos = (arr) => {
  const vids = shuffled(arr.filter((x) => typeof x !== "string"));
  const imgs = shuffled(arr.filter((x) => typeof x === "string"));
  const out = [];
  const per = Math.ceil(imgs.length / (vids.length + 1));
  let idx = 0;
  for (let g = 0; g <= vids.length; g++) {
    out.push(...imgs.slice(idx, idx + per));
    idx += per;
    if (g < vids.length) out.push(vids[g]);
  }
  return out;
};

const CONCEPT_IMGS = spreadWithVideos([
  "/images/about-vegan-burgler.webp",
  "/images/about-oops-fingers.webp",
  "/images/concept-come-n-eat.webp",
  "/images/concept-debs-doughnuts.webp",
  "/images/concept-bmas-house.webp",
  "/images/concept-soul-rolls.webp",
  { type: "video", src: "/videos/food-court-stadium.mp4", webm: "/videos/food-court-stadium.webm" },
  { type: "video", src: "/videos/food-court-contained-1.mp4", webm: "/videos/food-court-contained-1.webm" },
  { type: "video", src: "/videos/food-court-contained-2.mp4", webm: "/videos/food-court-contained-2.webm" },
  "/images/unit-biscuit.webp",
  "/images/concept-wangs.webp",
  "/images/concept-shuckit.webp",
  "/images/unit-orange-front.webp",
  "/images/kitchen-exterior.webp",
  "/images/unit-white.webp",
]);

// Cinematic hero video (7.5s seamless loop). Fallback photo sequence runs if this is null.
const HERO_VIDEO_SRC = "/videos/hero.mp4";
const HERO_POSTER = "/images/hero-poster.webp";

const trackCTA = (label) => { if (window.gtag) window.gtag("event", "cta_click", { event_category: "engagement", event_label: label }); };

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

function ParallaxImage({ src, alt, speed = 0.15, className = "", style = {} }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * -100, speed * 100]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`} style={style}>
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-full object-cover scale-110" loading="lazy" />
    </div>
  );
}

// SECTION 09 helper — auto-scrolling infinite marquee with mouse-drag and hover pause.
// Content renders twice; scrollLeft wraps at the halfway point for a seamless loop.
// Auto-scroll is disabled entirely for reduced-motion users (drag/swipe still work).
function MarqueeStrip({ items }) {
  const ref = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let hover = false;
    let raf;
    const normalize = () => {
      const half = el.scrollWidth / 2;
      if (half <= 0) return;
      if (el.scrollLeft >= half) el.scrollLeft -= half;
      else if (el.scrollLeft < 0) el.scrollLeft += half;
    };
    const step = () => {
      if (!reduce && !hover && !drag.current.down) {
        el.scrollLeft += 0.6;
        normalize();
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const onScroll = () => normalize();
    const onEnter = () => { hover = true; };
    const onLeave = () => { hover = false; };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    drag.current = { down: true, startX: e.clientX, startScroll: ref.current.scrollLeft };
    ref.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    ref.current.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };
  const endDrag = () => { drag.current.down = false; };

  return (
    <div
      ref={ref}
      className="flex gap-5 overflow-x-auto px-6 md:px-12 lg:px-24 pb-4 select-none cursor-grab active:cursor-grabbing"
      style={{ scrollbarWidth: "none" }}
      data-testid="market-gallery-strip"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {[...items, ...items].map((item, i) => (
        <motion.div
          key={i}
          aria-hidden={i >= items.length}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: Math.min(i * 0.06, 0.3), duration: 0.5 }}
          className="flex-shrink-0 w-[82vw] sm:w-[55vw] lg:w-[38vw]"
        >
          {typeof item === "string" ? (
            <img src={item} alt="The PadSite kitchen platform finished for a different restaurant concept" className="w-full h-52 md:h-72 object-cover pointer-events-none" loading="lazy" draggable={false} />
          ) : (
            <video autoPlay muted loop playsInline className="w-full h-52 md:h-72 object-cover pointer-events-none" data-testid={i < items.length ? "market-strip-video" : undefined}>
              <source src={item.src} type="video/mp4" />
              <source src={item.webm} type="video/webm" />
            </video>
          )}
        </motion.div>
      ))}
    </div>
  );
}

// SECTION 02 helper — interactive before/after slider (touch + mouse)
function BeforeAfterSlider({ before, after }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative w-full overflow-hidden select-none cursor-ew-resize"
      style={{ touchAction: "pan-y" }}
      onPointerDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
      onPointerMove={(e) => { if (dragging.current) setFromClientX(e.clientX); }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerLeave={() => { dragging.current = false; }}
    >
      <img src={after} alt="After — the same spaces operating as a branded PadSite restaurant" className="w-full h-auto block" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt="Before — ordinary unused parking spaces" className="w-full h-full object-cover block" draggable={false} />
      </div>
      {/* Handle */}
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5" style={{ backgroundColor: "#d4782f" }} />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: "#d4782f", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
          <ChevronsLeftRight size={18} color="#FFFFFF" />
        </div>
      </div>
      <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5" style={{ backgroundColor: "rgba(45,45,45,0.75)", color: "#FFFFFF" }}>Before</span>
      <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5" style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}>After</span>
    </div>
  );
}

// SECTION 01 — cinematic hero fallback (replaced by video when HERO_VIDEO_SRC is set)
function HeroVisual() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (HERO_VIDEO_SRC) return;
    const t = setInterval(() => setPhase((p) => (p + 1) % 3), 4500);
    return () => clearInterval(t);
  }, []);

  if (HERO_VIDEO_SRC) {
    return (
      <>
        {/* Mobile only: blurred fill so the video can zoom out (object-contain) without letterbox bars */}
        <img
          src={HERO_POSTER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-70 md:hidden"
        />
        <video
          className="absolute inset-0 w-full h-full object-contain object-[center_30%] md:object-cover md:object-center"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden="true"
          poster={HERO_POSTER}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
          <source src="/videos/hero.webm" type="video/webm" />
        </video>
      </>
    );
  }

  return (
    <motion.div
      className="absolute inset-0"
      animate={{ scale: [1, 1.14] }}
      transition={{ duration: 22, repeat: Infinity, repeatType: "mirror", ease: "linear" }}
      data-testid="hero-visual"
    >
      {/* Phase 0: the ordinary lot, quiet */}
      <img src={ATLANTA_IMG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms]"
        style={{ opacity: phase === 0 ? 1 : 0, filter: "grayscale(0.7) brightness(0.6)" }} />
      {/* Phase 1: the PadSite kitchen deployed, lights on at dusk */}
      <img src={DUSK_IMG} alt="A PadSite commercial kitchen illuminated at dusk in a metro Atlanta parking lot" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms]"
        style={{ opacity: phase === 1 ? 1 : 0 }} />
      {/* Phase 2: operating — lit service windows, menu boards, planters */}
      <img src={GOLDEN_IMG} alt="PadSite kitchen operating at golden hour with illuminated service windows and menu boards" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms]"
        style={{ opacity: phase === 2 ? 1 : 0 }} />
    </motion.div>
  );
}

export default function HomePage() {
  // Section 05 — big idea crossfade
  const ideaRef = useRef(null);
  const { scrollYProgress: ideaScroll } = useScroll({ target: ideaRef, offset: ["start start", "end end"] });
  const ideaFirstOpacity = useTransform(ideaScroll, [0, 0.38], [1, 0]);
  const ideaFirstY = useTransform(ideaScroll, [0, 0.38], [0, -40]);
  const ideaSecondOpacity = useTransform(ideaScroll, [0.55, 0.85], [0, 1]);
  const ideaSecondY = useTransform(ideaScroll, [0.55, 0.85], [40, 0]);

  return (
    <div data-testid="home-page" style={{ backgroundColor: "#2d2d2d" }}>
      {/* ====== SECTION 01 — CINEMATIC HERO ====== */}
      <section data-testid="cinematic-hero" className="relative overflow-hidden min-h-[100svh] flex items-end" style={{ backgroundColor: "#2d2d2d" }}>
        <HeroVisual />
        {/* Scrim: clear over the unit (the focal point), dark only at the bottom parking-lot strip where the text sits */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(45,45,45,0.5) 0%, rgba(45,45,45,0.05) 28%, rgba(45,45,45,0.02) 52%, rgba(45,45,45,0.55) 76%, rgba(45,45,45,0.9) 100%)" }} />

        <div className="relative w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-28 pb-10 md:pb-14">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.h1 variants={fadeUp} className="font-bold tracking-tighter leading-[1.02] mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span className="block text-4xl sm:text-6xl lg:text-7xl" style={{ color: "#FFFFFF" }}>THIS COULD BE</span>
              <span className="block text-4xl sm:text-6xl lg:text-7xl" style={{ color: "#d4782f" }}>A RESTAURANT</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-xl mb-8" style={{ color: "rgba(255,255,255,0.85)" }}>
              Commercial kitchens deployed where customers already are.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link
                to="/contact?tab=operator"
                onClick={() => trackCTA("hero_want_kitchen")}
                data-testid="hero-kitchen-cta"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 font-bold tracking-wide text-sm md:text-base transition-all glow-btn rounded-none"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              >
                I Want a Kitchen <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact?tab=landlord"
                onClick={() => trackCTA("hero_have_property")}
                data-testid="hero-property-cta"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 font-bold tracking-wide text-sm md:text-base rounded-none border transition-all"
                style={{ borderColor: "rgba(255,255,255,0.4)", color: "#FFFFFF", backgroundColor: "rgba(45,45,45,0.35)", backdropFilter: "blur(8px)" }}
              >
                I Have a Property
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold"
              style={{ color: "rgba(255,255,255,0.6)", wordSpacing: "0.6em" }}
              data-testid="hero-tagline"
            >
              Develop Deploy Grow
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ====== SECTION 02 — TRANSFORMATION (before/after) ====== */}
      <section data-testid="transformation" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-16 md:pt-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="mb-10 text-center">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#2d2d2d" }}>
              FROM PARKING SPACE<br />TO <span style={{ color: "#d4782f" }}>RESTAURANT</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base md:text-lg mt-5" style={{ color: "#3a3a3a" }}>
              Five parking spaces. One restaurant opportunity.
            </motion.p>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }} className="w-[94vw] md:w-[85vw] mx-auto pb-16 md:pb-24">
          <BeforeAfterSlider before={BEFORE_IMG} after={AFTER_IMG} />
          <p className="text-center text-xs uppercase tracking-[0.2em] font-semibold mt-4" style={{ color: "#628ea2" }}>Drag to compare</p>
        </motion.div>
      </section>

      {/* ====== SECTION 04 — ENGINEERED INFRASTRUCTURE ====== */}
      <section data-testid="engineered-infrastructure" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: "#628ea2" }}>
                Engineered Infrastructure
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-bold tracking-tighter leading-[1.08] mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#2d2d2d" }}>
                NOT A CONTAINER<br />A <span style={{ color: "#d4782f" }}>PLATFORM</span>
              </motion.h2>
              <div>
                {[
                  "10' Commercial Hood",
                  "Fire Suppression",
                  "200A Electrical System",
                  "Commercial Plumbing",
                  "Grease Trap",
                  "Dual HVAC Systems",
                  "Employee Restroom",
                ].map((spec, i) => (
                  <motion.div
                    key={spec}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="flex items-center gap-4 py-3 border-b"
                    style={{ borderColor: "rgba(45,45,45,0.1)" }}
                    data-testid={`spec-${i}`}
                  >
                    <motion.span
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 + 0.15, duration: 0.5 }}
                      className="h-px w-8 origin-left flex-shrink-0"
                      style={{ backgroundColor: "#d4782f" }}
                    />
                    <p className="text-base md:text-lg font-semibold tracking-wide" style={{ color: "#2d2d2d" }}>{spec}</p>
                  </motion.div>
                ))}
              </div>
              <motion.p variants={fadeUp} className="text-sm mt-8 leading-relaxed max-w-md" style={{ color: "#3a3a3a" }}>
                Every PadSite kitchen is built to the same engineered standard and delivered ready for the operator's own cooking equipment.
              </motion.p>
            </motion.div>
            <div className="space-y-8">
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#628ea2" }}>Kitchen Interior – View #1</p>
                <img src={KITCHEN_VIEW1_IMG} alt="PadSite kitchen interior view 1 — labeled hood wall with fire suppression, HVAC, electrical panel, plumbing and grease trap" className="w-full h-auto" loading="lazy" data-testid="infra-view-1" />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.1 }}>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#628ea2" }}>Kitchen Interior – View #2</p>
                <img src={KITCHEN_VIEW2_IMG} alt="PadSite kitchen interior view 2 — labeled window wall with FRP walls, hub drains and epoxy floors" className="w-full h-auto" loading="lazy" data-testid="infra-view-2" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 05 — BIG IDEA ====== */}
      <section ref={ideaRef} data-testid="big-idea" className="relative" style={{ height: "130vh", backgroundColor: "#d4782f" }}>
        <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
          <motion.h2
            style={{ opacity: ideaFirstOpacity, y: ideaFirstY, fontFamily: "'Space Grotesk', sans-serif" }}
            className="absolute px-6 text-center font-bold tracking-tighter leading-[1.02] text-5xl sm:text-7xl lg:text-[8.5rem] text-white"
            data-testid="big-idea-first"
          >
            RESTAURANTS<br />DON'T NEED<br />3,000 SQUARE FEET
          </motion.h2>
          <motion.h2
            style={{ opacity: ideaSecondOpacity, y: ideaSecondY, fontFamily: "'Space Grotesk', sans-serif" }}
            className="absolute px-6 text-center font-bold tracking-tighter leading-[1.02] text-5xl sm:text-7xl lg:text-[8.5rem] text-white"
            data-testid="big-idea-second"
          >
            THEY NEED<br />THE RIGHT<br /><span style={{ color: "#2d2d2d" }}>320 SF</span>
          </motion.h2>
        </div>
      </section>

      {/* ====== SECTION 05B — PADSITE IN THE MARKET ====== */}
      <section data-testid="market-gallery" className="py-16 md:py-24 overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 mb-10">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#2d2d2d" }}
          >
            ONE PLATFORM<br /><span style={{ color: "#d4782f" }}>MANY RESTAURANTS</span>
          </motion.h2>
        </div>
        <MarqueeStrip items={CONCEPT_IMGS} />
        <p className="text-center text-xs uppercase tracking-[0.2em] font-semibold mt-4" style={{ color: "#628ea2" }}>Scroll sideways</p>
      </section>

      {/* ====== SECTION 06 — RESTAURANT OPERATORS ====== */}
      <section data-testid="operators-section" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: "#d4782f" }}>
                For Restaurant Operators
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-bold tracking-tighter leading-[1.06] mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}>
                YOUR RESTAURANT<br />WITHOUT THE <span style={{ color: "#d4782f" }}>TRADITIONAL BUILDOUT</span>
              </motion.h2>
              <div className="space-y-4 mb-10">
                {[
                  "Ready-built commercial kitchen shell",
                  "Smaller footprint",
                  "Lower overhead",
                  "Faster path to opening",
                  "Metro Atlanta locations",
                ].map((t, i) => (
                  <motion.div key={t} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className="flex items-center gap-4">
                    <span className="h-px w-8 flex-shrink-0" style={{ backgroundColor: "#d4782f" }} />
                    <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.85)" }}>{t}</p>
                  </motion.div>
                ))}
              </div>
              <motion.div variants={fadeUp}>
                <Link
                  to="/contact?tab=operator"
                  onClick={() => trackCTA("operators_request_kitchen")}
                  data-testid="request-kitchen-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 font-bold tracking-wide text-sm transition-all glow-btn rounded-none"
                  style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
                >
                  Request a Kitchen <ArrowRight size={16} />
                </Link>
              </motion.div>
            </motion.div>
            <div className="grid grid-cols-3 gap-4">
              <ParallaxImage src={KITCHEN_IMG} alt="Inside a working PadSite commercial kitchen" speed={0.08} className="row-span-2 h-full min-h-[320px]" />
              <ParallaxImage src={FOOD_COURT_IMG} alt="A bustling container food court built with PadSite-style kitchens at dusk" speed={0.14} className="col-span-2 h-48 md:h-64" />
              <ParallaxImage src={FOOD_IMG} alt="Steaks grilling over open flame — food served from a PadSite kitchen" speed={0.14} className="col-span-2 h-48 md:h-64" />
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 07 — PROPERTY OWNERS ====== */}
      <section data-testid="owners-section" className="relative overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-14 md:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center mb-10">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.06]" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#2d2d2d" }}>
              OWN COMMERCIAL PROPERTY?
            </motion.h2>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.06] mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#d4782f" }}>
              PUT UNUSED PARKING<br />TO WORK
            </motion.h2>
          </motion.div>

          {/* Visual equation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto mb-10">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center">
              <div className="h-52 md:h-64 overflow-hidden" style={{ backgroundColor: "#3a3a3a" }} data-testid="equation-spaces">
                <img src={SPACES_IMG} alt="Empty parking spaces in a retail parking lot at dusk" className="h-full w-full object-cover" loading="lazy" />
              </div>
              <p className="text-sm md:text-base font-bold uppercase tracking-[0.15em] mt-4" style={{ color: "#2d2d2d" }}>5 Parking Spaces</p>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.25 }} className="text-center text-xs uppercase tracking-[0.3em] font-bold" style={{ color: "#628ea2" }}>
              becomes
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }} className="text-center">
              <div className="h-52 md:h-64 overflow-hidden" style={{ backgroundColor: "#2d2d2d" }} data-testid="equation-restaurant">
                <img src={EQUATION_IMG} alt="A 40-foot PadSite kitchen with menu boards deployed in a parking lot" className="h-full w-full object-cover" loading="lazy" />
              </div>
              <p className="text-sm md:text-base font-bold uppercase tracking-[0.15em] mt-4" style={{ color: "#d4782f" }}>1 Restaurant</p>
            </motion.div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center">
            <motion.p variants={fadeUp} className="text-base md:text-lg max-w-2xl mx-auto mb-8" style={{ color: "#3a3a3a" }}>
              PadSite handles development, permitting coordination, site preparation, deployment, and ongoing management.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link
                to="/contact?tab=landlord"
                onClick={() => trackCTA("owners_submit_property")}
                data-testid="submit-property-cta"
                className="inline-flex items-center gap-3 px-8 py-4 font-bold tracking-wide text-sm transition-all glow-btn rounded-none"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              >
                Submit a Property <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ====== SECTION 08 — COMPACT CLOSER ====== */}
      <section data-testid="home-closer" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={stagger}
            className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8"
          >
            <motion.h2
              variants={fadeUp}
              className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.08]"
              style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#FFFFFF" }}
            >
              READY TO PUT A KITCHEN<br /><span style={{ color: "#d4782f" }}>TO WORK?</span>
            </motion.h2>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link
                to="/contact?tab=operator"
                onClick={() => trackCTA("closer_want_kitchen")}
                data-testid="closer-kitchen-cta"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 font-bold tracking-wide text-sm transition-all glow-btn rounded-none"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              >
                I Want a Kitchen <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact?tab=landlord"
                onClick={() => trackCTA("closer_have_property")}
                data-testid="closer-property-cta"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 font-bold tracking-wide text-sm rounded-none border transition-all"
                style={{ borderColor: "rgba(255,255,255,0.4)", color: "#FFFFFF", backgroundColor: "rgba(255,255,255,0.06)" }}
              >
                I Have a Property
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
